import { defineStore } from 'pinia'
import operatingstatementApi, {
  type EditableOperatingStatementData
} from '@/api/operatingstatement'
import { notStarted, loading, loaded, error, isLoaded, type Loading } from '@/utils/loading'
import type { OperatingStatementAttributes } from 'common'
import { useStripeStore } from '@/stores/stripe'
import { useUserStore } from '@/stores/user'

/**
 * List of BWE OS Parser Beta List
 George Somers - auth0|69c170e621ea055314a0a61d
 Bella Barrios - auth0|699f366e46a7e37f29883539
 Bobby Trapnell - auth0|69a5a50107191e7f9c255afd
 Allison Eisendrath - auth0|692f8ad152fea8af8fcb7b2a
 Eric Riedmaier - auth0|695e948f762beeec39064037
 Hank Dalle Molle - auth0|6967ee5467de0fc3563c7e88
 Zach Weston - auth0|6967ee3ece875818c5985c29
 Caleb Carter - auth0|69bd51db3899dcfe86c58a01
 Katie Harrop - auth0|69f3aebb04380e7abd6dd097
 Robert Ferrell - auth0|695fff5113035a5842c81d96
 Eric Tupta - auth0|69b966c2211bf40c32e04e17
 */
const OS_PARSER_BETA_USER_IDS = [
  'auth0|69c170e621ea055314a0a61d',
  'auth0|699f366e46a7e37f29883539',
  'auth0|69a5a50107191e7f9c255afd',
  'auth0|692f8ad152fea8af8fcb7b2a',
  'auth0|695e948f762beeec39064037',
  'auth0|6967ee5467de0fc3563c7e88',
  'auth0|6967ee3ece875818c5985c29',
  'auth0|69bd51db3899dcfe86c58a01',
  'auth0|69f3aebb04380e7abd6dd097',
  'auth0|695fff5113035a5842c81d96',
  'auth0|69b966c2211bf40c32e04e17'
]

export const useOperatingStatementStore = defineStore('operatingstatement', {
  state: () => ({
    operatingStatements: notStarted as Loading<OperatingStatementAttributes[]>,
    currentOperatingStatement: notStarted as Loading<OperatingStatementAttributes>
  }),
  actions: {
    async index(): Promise<void> {
      this.operatingStatements = loading
      try {
        const operatingStatements = await operatingstatementApi.index()
        this.operatingStatements = loaded(operatingStatements.data)
      } catch (err) {
        console.error('Failed to fetch Operating Statement:', err)
        this.operatingStatements = error(err as Error)
      }
    },
    async getOperatingStatement(id: number): Promise<Loading<OperatingStatementAttributes>> {
      this.currentOperatingStatement = loading
      try {
        const os = await operatingstatementApi.show(id)
        this.currentOperatingStatement = loaded(os)
        return this.currentOperatingStatement
      } catch (err) {
        console.error('Failed to fetch Operating Statement:', err)
        this.currentOperatingStatement = error(err as Error)
        return this.currentOperatingStatement
      }
    },
    async create(
      data: EditableOperatingStatementData
    ): Promise<OperatingStatementAttributes | null> {
      try {
        const created = await operatingstatementApi.create(data)
        if (isLoaded(this.operatingStatements)) {
          this.operatingStatements.data.unshift(created)
        }
        return created
      } catch (err) {
        console.error('Failed to create Operating Statement:', err)
        return null
      }
    },
    async update(
      id: number,
      data: EditableOperatingStatementData
    ): Promise<OperatingStatementAttributes | null> {
      try {
        const updated = await operatingstatementApi.update(id, data)
        if (isLoaded(this.operatingStatements)) {
          const index = this.operatingStatements.data.findIndex((a) => a.id === id)
          if (index !== -1) {
            this.operatingStatements.data[index] = updated
          }
        }
        this.currentOperatingStatement = loaded(updated)
        return updated
      } catch (err) {
        console.error('Failed to update Operating Statement:', err)
        return null
      }
    },
    async destroy(id: number): Promise<boolean> {
      try {
        await operatingstatementApi.destroy(id)
        if (isLoaded(this.operatingStatements)) {
          this.operatingStatements.data = this.operatingStatements.data.filter((a) => a.id !== id)
        }
        return true
      } catch (err) {
        console.error('Failed to delete Operating Statement:', err)
        return false
      }
    },

    ...operatingstatementApi.parseOperatingStatement
  },
  getters: {
    getCurrentOperatingStatement(state): Loading<OperatingStatementAttributes> {
      return state.currentOperatingStatement
    },
    hasOsParserAccess(): boolean {
      const userStore = useUserStore()
      return (
        useStripeStore().hasRentRollParser &&
        (userStore.isHelloDataEmployee ||
          OS_PARSER_BETA_USER_IDS.includes(userStore.user?.sub ?? ''))
      )
    }
  }
})
